import { Button, Card, CardBody, CardHeader, CardTitle, DataBlock } from '@/common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import MassBalanceCalculatorForm from './MassBalanceCalculatorForm'
import { Drawer } from '@/shared/components'
import { z } from 'zod'
import { RATIO_OF_MOLLAR_MASSES } from '@/features/reports/formulas/constants'

export default function EditMaterialOutputDialog({
  isOpen,
  close,
  initialValues,
  cnCode,
  save
}: {
  isOpen: boolean
  close: () => void
  cnCode: string
  initialValues: {
    quantity: number
    carbonContent: number
    emission: number
  } | null
  save: (
    cnCode: string,
    item: {
      quantity: number
      carbonContent: number
      emission: number
    }
  ) => void
}) {
  const { t, numberFormatter3Decimals } = useI18n()
  const [state, setState] = useState({
    quantity: initialValues ? String(initialValues.quantity) : '',
    carbonContent: initialValues ? String(initialValues.carbonContent) : ''
  })

  const schema = useMemo(
    () =>
      z
        .object({
          quantity: z.number().nullable(),
          carbonContent: z.number().gte(0),
          emission: z.number().gte(0)
        })
        .refine(({ quantity }) => quantity !== null, {
          message: t('common:validations.quantityRequiredZeroAllowed'),
          path: ['quantity']
        }),
    [t]
  )

  const emission = useMemo(() => {
    const quantity = Number(state.quantity || 0)
    const carbonContent = Number(state.carbonContent || 0)
    return quantity * carbonContent * RATIO_OF_MOLLAR_MASSES
  }, [state])

  const payload = useMemo(() => {
    return {
      quantity: state.quantity === '' ? null : Number(state.quantity),
      carbonContent: state.carbonContent === '' ? 0 : Number(state.carbonContent),
      emission
    }
  }, [state])

  const isValid = useMemo(() => {
    const result = schema.safeParse(payload)
    return result.success
  }, [payload, schema])

  const onSave = useCallback(() => {
    if (!isValid) {return}
    save(cnCode, {
      quantity: Number(state.quantity || 0),
      carbonContent: Number(state.carbonContent || 0),
      emission
    })
  }, [cnCode, save, isValid, state, emission])

  const onQuantityChange = (quantity: string) => {
    setState({
      ...state,
      quantity
    })
  }

  const onCarbonContentChange = (carbonContent: string) => {
    setState({
      ...state,
      carbonContent
    })
  }

  const formattedEmission = useMemo(() => {
    return `${numberFormatter3Decimals(emission)} ${UnitLabel.tCO2PerT}`
  }, [emission, numberFormatter3Decimals])

  useEffect(() => {
    if (isOpen && initialValues) {
      setState({
        quantity: String(initialValues.quantity),
        carbonContent: String(initialValues.carbonContent)
      })
    }
  }, [isOpen, initialValues?.quantity, initialValues?.carbonContent])

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card className="min-w-[400px]">
        <CardHeader>
          <CardTitle title={t('report:massBalance.editMaterialOutputDialog.title')} />
        </CardHeader>
        <CardBody className="space-y-4 p-4">
          <div className="space-y-4">
            <MassBalanceCalculatorForm
              quantity={state.quantity}
              carbonContent={state.carbonContent}
              onQuantityChange={onQuantityChange}
              onCarbonContentChange={onCarbonContentChange}
            />
            <DataBlock label={t('report:common.emission')} data={formattedEmission} />
          </div>
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 p-4">
          <Button variant="subtle" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="default" onClick={onSave} disabled={!isValid}>
            {t('common:actions.save')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
